/**
 * Admin -- Settings/Compliance (Wave 1b W1b-03 wired)
 *
 * Sections:
 *   1. Compliance Profile -- 13 frameworks with KILLER server-computed
 *      cascade-preview diff
 *   2. Data Residency -- region, cross-border policy, sub-processors
 *   3. Export Control -- ITAR / EAR / US Person gate / classification marking
 *   4. Retention Matrix -- per-category retention with framework floor
 *
 * Mock data has been removed. First paint pulls from
 * /api/v1/admin/settings/compliance. Cascade preview is computed
 * server-side via POST /cascade-preview. Apply uses POST /apply with
 * the user-collected acknowledgments. Retention floor is enforced at
 * the API; PATCH /retention/:category returns 422 when a value is
 * below the active framework floor.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ShieldCheck, Globe, Lock, Clock, FileCheck, Check,
  AlertTriangle, Sparkles, Plus, Minus, Loader2, Save,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  SettingField, SectionCard,
} from '@/components/admin';
import { apiClient } from '@/lib/api-client';
import { TypedConfirmDialog } from '@/components/admin/typed-confirm-dialog';

export const Route = createFileRoute('/admin/settings/compliance')({
  component: CompliancePage,
});

type FrameworkId =
  | 'soc2' | 'iso27001' | 'iso27017' | 'iso27018' | 'iso27701'
  | 'fedramp' | 'cmmc_l2' | 'itar' | 'ear' | 'hipaa' | 'gdpr'
  | 'ccpa' | 'pci_dss';

type RetentionCategory =
  | 'audit_logs' | 'api_logs' | 'conversations' | 'documents'
  | 'incidents' | 'sso_events' | 'webhook_deliveries' | 'backup_metadata'
  | 'classification_markings' | 'compliance_evidence';

interface FrameworkRow {
  id: FrameworkId;
  name: string;
  description: string;
  active: boolean;
  retentionFloors: Partial<Record<RetentionCategory, number>>;
  requiresUsPersonGate: boolean;
  blocksNonItarChatOps: boolean;
  requiresClassificationMarking: boolean;
}

interface SubProcessor {
  id: string;
  name: string;
  purpose: string;
  region: string;
  onPremOnly: boolean;
  contactEmail: string | null;
  contractRef: string | null;
  createdAt: string;
}

interface ComplianceGetResponse {
  frameworks: FrameworkRow[];
  activeFrameworks: FrameworkId[];
  residency: Record<string, unknown>;
  exportControl: Record<string, unknown>;
  retention: Record<RetentionCategory, number>;
  retentionFloors: Record<RetentionCategory, number>;
  subProcessors: SubProcessor[];
}

interface CascadeImpact {
  severity: 'critical' | 'warning' | 'info';
  category: string;
  summary: string;
  beforeValue: string;
  afterValue: string;
  affectedResource?: string;
  ackRequired: boolean;
}

interface CascadePreviewResponse {
  beforeFrameworks: FrameworkId[];
  afterFrameworks: FrameworkId[];
  impacts: CascadeImpact[];
  criticalCount: number;
}

const RETENTION_LABEL: Record<RetentionCategory, string> = {
  audit_logs: 'Audit logs',
  api_logs: 'API access logs',
  conversations: 'Conversations',
  documents: 'Documents',
  incidents: 'Incidents',
  sso_events: 'SSO events',
  webhook_deliveries: 'Webhook deliveries',
  backup_metadata: 'Backup metadata',
  classification_markings: 'Classification markings',
  compliance_evidence: 'Compliance evidence',
};

const RETENTION_ORDER: RetentionCategory[] = [
  'audit_logs', 'api_logs', 'conversations', 'documents', 'incidents',
  'sso_events', 'webhook_deliveries', 'backup_metadata',
  'classification_markings', 'compliance_evidence',
];

function CompliancePage() {
  const [data, setData] = useState<ComplianceGetResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedFrameworks, setSelectedFrameworks] = useState<FrameworkId[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Residency / export-control controlled fields.
  const [crossBorderPolicy, setCrossBorderPolicy] = useState<
    'allow' | 'restricted' | 'blocked'
  >('restricted');
  const [primaryRegion, setPrimaryRegion] = useState('us-east');
  const [savingResidency, setSavingResidency] = useState(false);
  const [confirmResidencyOpen, setConfirmResidencyOpen] = useState(false);

  const [earDefaultEccn, setEarDefaultEccn] = useState('');
  const [usPersonGateEnforced, setUsPersonGateEnforced] = useState(false);
  const [deemedExportPreventionEnabled, setDeemedExportPreventionEnabled] =
    useState(false);
  const [classificationMarkingRequired, setClassificationMarkingRequired] =
    useState(false);
  const [classificationMarkingScheme, setClassificationMarkingScheme] =
    useState<'DoD-5220' | 'custom' | 'none'>('none');
  const [savingExport, setSavingExport] = useState(false);

  // Sub-processor add dialog
  const [subProcOpen, setSubProcOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const r = await apiClient.get<ComplianceGetResponse>(
        '/admin/settings/compliance/'
      );
      setData(r);
      setSelectedFrameworks(r.activeFrameworks);
      const res = r.residency as Record<string, unknown>;
      setPrimaryRegion((res.primary_region as string | undefined) ?? 'us-east');
      const cbp = res.cross_border_policy as
        | 'allow' | 'restricted' | 'blocked' | undefined;
      setCrossBorderPolicy(cbp ?? 'restricted');
      const exp = r.exportControl as Record<string, unknown>;
      setEarDefaultEccn((exp.ear_default_eccn as string | undefined) ?? '');
      setUsPersonGateEnforced(
        Boolean(exp.us_person_gate_enforced) ||
          r.activeFrameworks.includes('itar')
      );
      setDeemedExportPreventionEnabled(
        Boolean(exp.deemed_export_prevention_enabled)
      );
      setClassificationMarkingRequired(
        Boolean(exp.classification_marking_required)
      );
      setClassificationMarkingScheme(
        (exp.classification_marking_scheme as
          | 'DoD-5220' | 'custom' | 'none' | undefined) ?? 'none'
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load compliance';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const hasFrameworkChanges = useMemo(() => {
    if (!data) return false;
    const cur = new Set(data.activeFrameworks);
    const next = new Set(selectedFrameworks);
    if (cur.size !== next.size) return true;
    for (const f of cur) if (!next.has(f)) return true;
    return false;
  }, [data, selectedFrameworks]);

  const toggleFramework = (f: FrameworkId) => {
    setSelectedFrameworks((prev) =>
      prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]
    );
  };

  async function saveResidency(reason = 'Updated from admin UI') {
    try {
      setSavingResidency(true);
      await apiClient.patch('/admin/settings/compliance/data-residency', {
        primaryRegion,
        crossBorderPolicy,
        reason,
      });
      toast.success('Data residency updated');
      setConfirmResidencyOpen(false);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    } finally {
      setSavingResidency(false);
    }
  }

  // Spec §3.3 line 280: changing the cross-border policy (a regulated
  // export-control field) requires a typed-phrase + reason gate. Only
  // gate when the policy actually changes from the loaded value; a
  // region-only edit saves directly.
  function handleSaveResidency() {
    const loadedPolicy =
      (data?.residency?.['cross_border_policy'] as string | undefined) ??
      'restricted';
    if (crossBorderPolicy !== loadedPolicy) {
      setConfirmResidencyOpen(true);
    } else {
      void saveResidency();
    }
  }

  async function saveExport() {
    try {
      setSavingExport(true);
      await apiClient.patch('/admin/settings/compliance/export-control', {
        earDefaultEccn: earDefaultEccn.trim() || undefined,
        usPersonGateEnforced,
        deemedExportPreventionEnabled,
        classificationMarkingRequired,
        classificationMarkingScheme,
        reason: 'Updated from admin UI',
      });
      toast.success('Export control settings updated');
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    } finally {
      setSavingExport(false);
    }
  }

  async function deleteSubProcessor(id: string, name: string) {
    if (!confirm(`Remove sub-processor "${name}"?`)) return;
    try {
      await apiClient.delete(`/admin/settings/compliance/sub-processors/${id}`);
      toast.success(`Sub-processor "${name}" removed`);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Remove failed';
      toast.error(msg);
    }
  }

  if (loading || !data) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading compliance...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Compliance</h1>
        <p className="text-xs text-muted-foreground mt-1">
          Frameworks, data residency, export control, and retention policies.
        </p>
      </div>

      {/* 1. Compliance Profile */}
      <SectionCard
        title="Compliance Profile"
        description="Frameworks this tenant is being assessed against. Changes cascade to retention, MFA, channel routing, and signing-policy settings."
        icon={ShieldCheck}
        actions={
          hasFrameworkChanges && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedFrameworks(data.activeFrameworks)}
                title="Discard pending framework changes"
              >
                Discard
              </Button>
              <Button
                size="sm"
                onClick={() => setPreviewOpen(true)}
                title="Preview the cascade impact (server-computed)"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                Preview changes
              </Button>
            </div>
          )
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {data.frameworks.map((f) => {
            const checked = selectedFrameworks.includes(f.id);
            return (
              <label
                key={f.id}
                className={cn(
                  'flex items-start gap-2 rounded-md border p-2.5 cursor-pointer transition-colors',
                  checked
                    ? 'bg-primary/5 border-primary/30'
                    : 'hover:bg-muted/50'
                )}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggleFramework(f.id)}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold">{f.name}</span>
                    <Badge
                      variant="outline"
                      className="text-[9px] px-1 h-4 uppercase"
                    >
                      {f.id}
                    </Badge>
                    {f.requiresUsPersonGate && (
                      <Badge
                        variant="outline"
                        className="text-[9px] px-1 h-4 bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30"
                      >
                        US Person gate
                      </Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
                    {f.description}
                  </p>
                </div>
              </label>
            );
          })}
        </div>
      </SectionCard>

      {confirmResidencyOpen && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setConfirmResidencyOpen(false)}
          title="Change cross-border data policy"
          description={`Set the cross-border transfer policy to "${crossBorderPolicy}". This is a regulated export-control change recorded in the audit chain (GDPR Art. 44-49 / Schrems II).`}
          consequences={[
            'Cross-border transfer behavior changes tenant-wide immediately',
            'The change is hash-chained to the compliance audit log with your reason',
          ]}
          confirmPhrase={crossBorderPolicy}
          confirmLabel="Change policy"
          variant="warning"
          requireReason
          reasonPlaceholder="e.g. Signed SCCs with EU sub-processor, ticket COMP-1042"
          onConfirm={(payload) =>
            saveResidency(payload.reason ?? 'Cross-border policy changed from admin UI')
          }
        />
      )}

      {/* 2. Data Residency */}
      <SectionCard
        title="Data Residency"
        description="Where data physically lives and can be processed."
        icon={Globe}
        actions={
          <Button
            size="sm"
            className="h-8 gap-1.5"
            onClick={handleSaveResidency}
            disabled={savingResidency}
            title="Save residency changes"
          >
            {savingResidency ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save changes
          </Button>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <SettingField
            label="Primary region"
            description="Primary processing and storage region."
          >
            <Select value={primaryRegion} onValueChange={setPrimaryRegion}>
              <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="us-east"    className="text-xs">US East</SelectItem>
                <SelectItem value="us-central" className="text-xs">US Central</SelectItem>
                <SelectItem value="us-west"    className="text-xs">US West</SelectItem>
                <SelectItem value="eu-west"    className="text-xs">EU West</SelectItem>
                <SelectItem value="eu-central" className="text-xs">EU Central</SelectItem>
                <SelectItem value="apac-east"  className="text-xs">APAC East</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>

          <SettingField
            label="Cross-border policy"
            description="Whether data may leave the primary region."
            complianceCitation="GDPR Article 44-49 / Schrems II"
          >
            <Select
              value={crossBorderPolicy}
              onValueChange={(v) => setCrossBorderPolicy(v as typeof crossBorderPolicy)}
            >
              <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="blocked"    className="text-xs">Blocked (strictest)</SelectItem>
                <SelectItem value="restricted" className="text-xs">Restricted (allow with SCC/BCR)</SelectItem>
                <SelectItem value="allow"      className="text-xs">Allow</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>
        </div>

        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
              Sub-processors ({data.subProcessors.length})
            </p>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px]"
              onClick={() => setSubProcOpen(true)}
              title="Add a new sub-processor"
            >
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>
          <div className="space-y-1.5">
            {data.subProcessors.length === 0 && (
              <p className="text-[11px] text-muted-foreground italic py-2">
                No sub-processors registered.
              </p>
            )}
            {data.subProcessors.map((sp) => (
              <div
                key={sp.id}
                className="flex items-center gap-3 rounded-md border p-2 text-xs"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{sp.name}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {sp.purpose} - {sp.region}
                    {sp.contactEmail && <> - {sp.contactEmail}</>}
                  </div>
                </div>
                {sp.onPremOnly && (
                  <Badge
                    variant="outline"
                    className="text-[9px] bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                  >
                    On-prem only
                  </Badge>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => deleteSubProcessor(sp.id, sp.name)}
                  title={`Remove "${sp.name}"`}
                  aria-label={`Remove sub-processor '${sp.name}'`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      {/* 3. Export Control */}
      <SectionCard
        title="Export Control"
        description="ITAR / EAR controls. ITAR-active: cannot disable US Person gate without first removing ITAR."
        icon={Lock}
        actions={
          <Button
            size="sm"
            className="h-8 gap-1.5"
            onClick={saveExport}
            disabled={savingExport}
            title="Save export-control changes"
          >
            {savingExport ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save changes
          </Button>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <SettingField
            label="ITAR active"
            description="Derived from compliance profile."
            readOnly
            readOnlyReason="Toggled via Compliance Profile"
          >
            <div className="flex items-center h-9 gap-2">
              <Switch
                checked={data.activeFrameworks.includes('itar')}
                disabled
              />
              <span className="text-xs text-muted-foreground">(derived)</span>
            </div>
          </SettingField>

          <SettingField
            label="Default EAR ECCN"
            description="Default Export Control Classification Number for uncategorized data."
            complianceCitation="EAR Section 740.17 (BIS Commerce)"
          >
            <Input
              value={earDefaultEccn}
              onChange={(e) => setEarDefaultEccn(e.target.value)}
              placeholder="e.g. EAR99"
              className="h-9 text-xs font-mono"
            />
          </SettingField>

          <SettingField
            label="US Person gate enforced"
            description="Blocks ITAR-controlled access unless user is verified US Person."
            complianceCitation="ITAR Section 120.15"
          >
            <div className="flex items-center h-9">
              <Switch
                checked={usPersonGateEnforced}
                onCheckedChange={setUsPersonGateEnforced}
                disabled={data.activeFrameworks.includes('itar')}
              />
              {data.activeFrameworks.includes('itar') && (
                <span className="text-[10px] text-muted-foreground ml-2">
                  Forced on by ITAR
                </span>
              )}
            </div>
          </SettingField>

          <SettingField
            label="Deemed export prevention"
            description="Blocks disclosure to non-US Persons during agent runs."
            complianceCitation="ITAR Section 120.17"
          >
            <div className="flex items-center h-9">
              <Switch
                checked={deemedExportPreventionEnabled}
                onCheckedChange={setDeemedExportPreventionEnabled}
              />
            </div>
          </SettingField>

          <SettingField
            label="Classification marking required"
            description="Requires every uploaded document to carry an explicit classification."
          >
            <div className="flex items-center h-9">
              <Switch
                checked={classificationMarkingRequired}
                onCheckedChange={setClassificationMarkingRequired}
              />
            </div>
          </SettingField>

          <SettingField
            label="Classification marking scheme"
            description="Standard for document classification labels."
          >
            <Select
              value={classificationMarkingScheme}
              onValueChange={(v) =>
                setClassificationMarkingScheme(v as typeof classificationMarkingScheme)
              }
            >
              <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="DoD-5220" className="text-xs">DoD 5220.22-M</SelectItem>
                <SelectItem value="custom"   className="text-xs">Custom scheme</SelectItem>
                <SelectItem value="none"     className="text-xs">No marking scheme</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>
        </div>
      </SectionCard>

      {/* 4. Retention Matrix */}
      <SectionCard
        title="Retention Matrix"
        description="How long each category of data is kept. Framework floors are enforced server-side - the API returns 422 if you go below."
        icon={Clock}
      >
        <div className="rounded-lg border overflow-hidden">
          <div className="grid grid-cols-[minmax(180px,1.5fr)_120px_140px_120px] gap-2 px-3 py-2 bg-muted/50 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
            <span>Category</span>
            <span>Floor (days)</span>
            <span>Current (days)</span>
            <span className="text-right">Save</span>
          </div>
          <div className="divide-y">
            {RETENTION_ORDER.map((cat) => (
              <RetentionRow
                key={cat}
                category={cat}
                floor={data.retentionFloors[cat] ?? 0}
                current={data.retention[cat] ?? 0}
                onSaved={refresh}
              />
            ))}
          </div>
        </div>
      </SectionCard>

      {/* Cascade preview dialog (server-driven) */}
      <CascadePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        currentActive={data.activeFrameworks}
        proposed={selectedFrameworks}
        onApplied={async () => {
          setPreviewOpen(false);
          await refresh();
        }}
      />

      {subProcOpen && (
        <SubProcessorAddDialog
          onClose={() => setSubProcOpen(false)}
          onAdded={async () => {
            setSubProcOpen(false);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

// ─── Retention row ──────────────────────────────────────────────────────────

function RetentionRow({
  category, floor, current, onSaved,
}: {
  category: RetentionCategory;
  floor: number;
  current: number;
  onSaved: () => Promise<void>;
}) {
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);
  const dirty = value !== current;
  const violatesFloor = value < floor;

  async function save() {
    if (violatesFloor) {
      toast.error(`Value must be at least ${floor} days (active framework floor).`);
      return;
    }
    try {
      setSaving(true);
      await apiClient.patch(
        `/admin/settings/compliance/retention/${category}`,
        {
          retentionDays: value,
          reason: 'Updated from admin UI',
        }
      );
      toast.success(
        `${RETENTION_LABEL[category]} retention updated to ${value} days`
      );
      await onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid grid-cols-[minmax(180px,1.5fr)_120px_140px_120px] gap-2 px-3 py-2.5 items-center text-xs">
      <div className="min-w-0">
        <div className="font-medium truncate">{RETENTION_LABEL[category]}</div>
      </div>
      <div className="text-muted-foreground tabular-nums">
        {floor.toLocaleString()}
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={value}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            if (!isNaN(n)) setValue(n);
          }}
          min={floor}
          className={cn(
            'h-7 w-24 text-xs font-mono',
            violatesFloor && 'border-red-500 text-red-600'
          )}
        />
        {violatesFloor && (
          <Badge
            variant="outline"
            className="text-[9px] bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30"
          >
            Below floor
          </Badge>
        )}
      </div>
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-[11px]"
          onClick={save}
          disabled={!dirty || saving || violatesFloor}
          title="Save retention change"
        >
          {saving ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Save className="h-3 w-3" />
          )}
        </Button>
      </div>
    </div>
  );
}

// ─── Cascade-preview dialog ─────────────────────────────────────────────────

function CascadePreviewDialog({
  open, onOpenChange, currentActive, proposed, onApplied,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  currentActive: FrameworkId[];
  proposed: FrameworkId[];
  onApplied: () => Promise<void>;
}) {
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<CascadePreviewResponse | null>(null);
  const [acks, setAcks] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [applying, setApplying] = useState(false);

  const added = proposed.filter((f) => !currentActive.includes(f));
  const removed = currentActive.filter((f) => !proposed.includes(f));

  // Recompute preview from server when dialog opens.
  useEffect(() => {
    if (!open) {
      setPreview(null);
      setAcks(new Set());
      setReason('');
      return;
    }
    (async () => {
      try {
        setPreviewLoading(true);
        const r = await apiClient.post<CascadePreviewResponse>(
          '/admin/settings/compliance/cascade-preview',
          { added, removed }
        );
        setPreview(r);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Preview failed';
        toast.error(msg);
      } finally {
        setPreviewLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const criticalImpacts = (preview?.impacts ?? []).filter((i) => i.ackRequired);
  const allAcked = criticalImpacts.every((i) =>
    acks.has(`${i.category}:${i.affectedResource ?? ''}`)
  );

  function toggleAck(impact: CascadeImpact) {
    const k = `${impact.category}:${impact.affectedResource ?? ''}`;
    setAcks((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  async function apply() {
    if (reason.trim().length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    if (!allAcked) {
      toast.error('Acknowledge all critical impacts first');
      return;
    }
    try {
      setApplying(true);
      await apiClient.post('/admin/settings/compliance/apply', {
        added,
        removed,
        acknowledgments: Array.from(acks),
        reason: reason.trim(),
      });
      toast.success('Compliance profile updated', {
        description: 'Cascading changes applied. Audit row recorded.',
      });
      await onApplied();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Apply failed';
      toast.error(msg);
    } finally {
      setApplying(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Compliance Profile - Change Preview
          </DialogTitle>
          <DialogDescription>
            Cascading impacts are computed server-side. Critical items
            require acknowledgment. Apply runs in a single transaction
            with reconcileSigningPolicy() afterward.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-6 space-y-5">
            {/* Diff summary */}
            <div className="grid grid-cols-2 gap-3">
              {added.length > 0 && (
                <Card className="border-emerald-500/40">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-2">
                      <Plus className="h-3.5 w-3.5" />
                      Adding ({added.length})
                    </div>
                    <div className="space-y-1">
                      {added.map((f) => (
                        <code
                          key={f}
                          className="block text-[11px] font-mono uppercase"
                        >
                          {f}
                        </code>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              {removed.length > 0 && (
                <Card className="border-red-500/40">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-red-700 dark:text-red-300 mb-2">
                      <Minus className="h-3.5 w-3.5" />
                      Removing ({removed.length})
                    </div>
                    <div className="space-y-1">
                      {removed.map((f) => (
                        <code
                          key={f}
                          className="block text-[11px] font-mono uppercase"
                        >
                          {f}
                        </code>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <Separator />

            {previewLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-6">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Computing cascade impacts...
              </div>
            ) : preview && preview.impacts.length === 0 ? (
              <div className="py-8 text-center">
                <Check className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
                <p className="text-sm font-medium">No cascading changes</p>
              </div>
            ) : preview ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold">
                    Cascading impacts ({preview.impacts.length})
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {criticalImpacts.length} require acknowledgment
                  </p>
                </div>

                <div className="space-y-2">
                  {preview.impacts.map((impact, idx) => {
                    const k = `${impact.category}:${impact.affectedResource ?? ''}`;
                    const isAcked = acks.has(k);
                    return (
                      <div
                        key={idx}
                        className={cn(
                          'rounded-md border p-3',
                          impact.severity === 'critical' && 'border-red-500/40 bg-red-500/5',
                          impact.severity === 'warning' && 'border-amber-500/40 bg-amber-500/5',
                          impact.severity === 'info' && 'border-border bg-muted/20'
                        )}
                      >
                        <div className="flex items-start gap-2">
                          <FileCheck className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                              <span className="text-xs font-semibold">
                                {impact.summary}
                              </span>
                              <Badge
                                variant="outline"
                                className={cn(
                                  'text-[9px]',
                                  impact.severity === 'critical' &&
                                    'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30',
                                  impact.severity === 'warning' &&
                                    'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
                                  impact.severity === 'info' &&
                                    'bg-muted text-muted-foreground border-border'
                                )}
                              >
                                {impact.severity}
                              </Badge>
                              <Badge
                                variant="outline"
                                className="text-[9px] capitalize"
                              >
                                {impact.category}
                              </Badge>
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed">
                              {impact.beforeValue} -&gt; {impact.afterValue}
                              {impact.affectedResource && (
                                <span className="ml-2">
                                  (resource:{' '}
                                  <code>{impact.affectedResource}</code>)
                                </span>
                              )}
                            </p>
                            {impact.ackRequired && (
                              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                                <Checkbox
                                  checked={isAcked}
                                  onCheckedChange={() => toggleAck(impact)}
                                />
                                <span className="text-[11px] font-medium">
                                  I acknowledge this change.
                                </span>
                              </label>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {criticalImpacts.length > 0 && !allAcked && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="text-xs text-amber-800 dark:text-amber-300">
                  {criticalImpacts.length - acks.size} of {criticalImpacts.length}{' '}
                  critical acknowledgments remaining.
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="cmpl-reason">Reason (required)</Label>
              <Textarea
                id="cmpl-reason"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Customer onboarding ITAR ack from compliance team"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/20">
          <div className="flex items-center justify-between w-full">
            <p className="text-[11px] text-muted-foreground">
              Audit row recorded on apply. Signing policy reconciles automatically.
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={applying}
              >
                Cancel
              </Button>
              <Button
                onClick={apply}
                disabled={applying || !allAcked || previewLoading}
                className={
                  !allAcked ? '' : 'bg-emerald-600 hover:bg-emerald-700'
                }
              >
                {applying && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                )}
                <Check className="h-4 w-4 mr-1.5" />
                Apply changes
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Sub-processor add dialog ───────────────────────────────────────────────

function SubProcessorAddDialog({
  onClose, onAdded,
}: {
  onClose: () => void;
  onAdded: () => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [region, setRegion] = useState('us-east');
  const [onPremOnly, setOnPremOnly] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim() || !purpose.trim() || !region.trim()) {
      toast.error('Name, purpose, and region are required');
      return;
    }
    try {
      setSaving(true);
      await apiClient.post('/admin/settings/compliance/sub-processors', {
        name: name.trim(),
        purpose: purpose.trim(),
        region: region.trim(),
        onPremOnly,
        contactEmail: contactEmail.trim() || undefined,
        reason: 'Added from admin UI',
      });
      toast.success('Sub-processor added');
      await onAdded();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Add failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add sub-processor</DialogTitle>
          <DialogDescription>
            Sub-processors are external services that handle tenant data
            (storage, telemetry, email, etc). Disclose under GDPR Article 28.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="sp-name">Name</Label>
            <Input
              id="sp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. AWS S3"
              className="h-9 text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sp-purpose">Purpose</Label>
            <Input
              id="sp-purpose"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="e.g. Object storage for backups"
              className="h-9 text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sp-region">Region</Label>
            <Input
              id="sp-region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder="e.g. us-east-1"
              className="h-9 text-xs font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sp-email">Contact email (optional)</Label>
            <Input
              id="sp-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="dpo@vendor.example"
              className="h-9 text-xs"
            />
          </div>
          <label className="flex items-center gap-2">
            <Checkbox
              checked={onPremOnly}
              onCheckedChange={(v) => setOnPremOnly(v === true)}
            />
            <span className="text-xs">On-prem only (cannot be reached from this deployment)</span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Add sub-processor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
